import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { LeftOutlined, LeftOutlined as LeftArrow, RightOutlined as RightArrow } from '@ant-design/icons';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/DBContext';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function RelatedArticles() {
  const navigate = useNavigate();
  const { fetchPDFs } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 items per page (3x3 grid)

  useEffect(() => {
    loadPDFs();
  }, []);

  const loadPDFs = async () => {
    setLoading(true);
    try {
      const pdfs = await fetchPDFs('advice'); // Fetch PDFs for 'advice' category
      setArticles(pdfs);
    } catch (error) {
      console.error('Error fetching PDFs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePdfClick = (pdfUrl, title) => {
    navigate(`/pdf?url=${encodeURIComponent(pdfUrl)}&title=${encodeURIComponent(title)}&downloadable=true&from=advice`);
  };

  // Calculate pagination
  const totalPages = Math.ceil(articles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArticles = articles.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl">
      <div style={{ marginBottom: '8px' }}>
        <Link to="/resources" className="flex items-center text-blue-500 hover:text-blue-700">
          <LeftOutlined className="mr-2" />
          Back
        </Link>
      </div>
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">Advice</h2>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      ) : articles.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <p className="text-gray-500">No PDFs available in this category.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {currentArticles.map((article) => (
              <div 
                key={article.id}
                className="relative flex flex-col h-[400px] overflow-hidden transition-transform duration-300 bg-white border rounded-lg shadow-sm hover:shadow-md hover:-translate-y-1"
              >
                <div className="h-1/2 relative overflow-hidden flex items-center justify-center bg-gray-50">
                  <Document
                    file={article.pdfUrl}
                    loading={<div className="text-gray-400">Loading...</div>}
                    error={<div className="text-red-400">Error loading PDF</div>}
                  >
                    <Page
                      pageNumber={1}
                      width={200}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<div className="text-gray-400">Loading page...</div>}
                    />
                  </Document>
                </div>
                <div className="h-1/2 p-4 flex flex-col justify-between">
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-800">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {article.description}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePdfClick(article.pdfUrl, article.title)}
                    className="inline-block px-6 py-2 text-sm font-medium text-white transition-colors duration-200 bg-blue-500 rounded-full hover:bg-blue-600"
                    aria-label={`Open ${article.title}`}
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-center mt-8 space-x-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-full ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              aria-label="Previous page"
            >
              <LeftArrow />
            </button>
            
            <div className="flex items-center space-x-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`w-8 h-8 rounded-full ${
                    currentPage === index + 1
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-full ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              aria-label="Next page"
            >
              <RightArrow />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default RelatedArticles;